import { IHwTypeBase, Subsystem } from '@dto/architecture.dto';
import { Tag, Tag as TagDTO, TagsResponseDTO } from '@dto/tag.dto';
import { getAllSubsystems } from 'src/content/tools/alerting/alertingService';
import { MenuNode } from 'src/model/MenuNode';
import { Log } from 'src/service/log.service';
import { GLOB } from 'src/util/Glob';
import { create } from 'zustand';
import { buildSearchRouteQuery, formatSearchQueryTitle } from '../domain/queryCodec';
import { SearchOption, SubsystemClassificationItem } from '../search.types';

import { BasicResponseDTO } from '@dto/basicResponse.dto';
import { RecentlySearched, RecentlySearchedResponseDTO } from "@dto/recentlySearched.dto";
import axios from 'axios';

const fetchRecentlySearched = async (
  userId: number,
  limit: number
): Promise<RecentlySearched[]> => {
  const response = await axios.get<RecentlySearchedResponseDTO>(
    `/api/search/v1/recently_searched?user_id=${userId}&limit=${limit}`
  );
  return response.data?.data || [];
};

const fetchTags = async (): Promise<Tag[]> => {
  const response = await axios.get<TagsResponseDTO>('/api/tag/v1');
  return response.data?.data || [];
};

interface ArchitectureResponse extends BasicResponseDTO {
  data: SubsystemClassificationItem[];
}

const fetchArchitectureData = async (): Promise<SubsystemClassificationItem[]> => {
  const response = await axios.get<ArchitectureResponse>(
    '/api/architecture/v1/subsystems/classification'
  );
  return response.data?.data || [];
};

export interface SearchHistoryNode extends MenuNode {
  date: Date;
  titleText: string;
}

interface SearchState {
  search: string;
  options: SearchOption[];
  isSearchVisible: boolean;
  setSearch: (search: string) => void;
  setOptions: (options: SearchOption[]) => void;
  showSearch: () => void;
  hideSearch: () => void;
  resetSearch: () => void;
  // Cross-page search state
  queryString: string;
  setQueryString: (queryString: string) => void;
  historyData: SearchHistoryNode[];
  setHistoryData: (historyData: SearchHistoryNode[]) => void;
  // Tags and architecture data
  availableTags: TagDTO[];
  availableTagsCategories: TagDTO[];
  availableArchitecture: SubsystemClassificationItem[];
  refreshTagsData: () => Promise<void>;
  loadArchitectureData: () => Promise<void>;
  reloadHistoryData: () => Promise<void>;
  // Label data for search
  hwTypes: IHwTypeBase[];
  subsystems: Subsystem[];
  isLabelDataLoading: boolean;
  loadLabelData: () => Promise<void>;
}

export const useSearchStore = create<SearchState>((set, get) => {
  let reloadSeq = 0;

  return {
    search: '',
    options: [],
    isSearchVisible: false,
    queryString: '',
    historyData: [],
    availableTags: [],
    availableTagsCategories: [],
    availableArchitecture: [],
    hwTypes: [],
    subsystems: [],
    isLabelDataLoading: false,
    setHistoryData: (historyData) => set({ historyData }),
    setSearch: (search) => set({ search, options: [] }),
    setOptions: (options) => set({ options }),
    showSearch: () => set({ isSearchVisible: true }),
    hideSearch: () => set({ isSearchVisible: false }),
    resetSearch: () => set({ search: '', options: [], queryString: '' }),
    setQueryString: (queryString) => set({ queryString }),

    refreshTagsData: async () => {
      try {
        const tags = await fetchTags();
        set({
          availableTags: tags.filter((obj) => obj.include_self),
          availableTagsCategories: tags.filter((obj) => !obj.include_self),
        });
      } catch (error) {
        Log.error('Failed to load tags data', error);
      }
    },

    loadArchitectureData: async () => {
      try {
        const architecture = await fetchArchitectureData();
        set({ availableArchitecture: architecture });
      } catch (error) {
        Log.error('Failed to load architecture data', error);
      }
    },

    reloadHistoryData: async () => {
      const mySeq = ++reloadSeq;
      const userId = GLOB.userInfo?.user_id;
      const limit = GLOB.userInfo?.configuration?.searchHistoryLimit || 10;

      if (!userId) {
        set({ historyData: [] });
        return;
      }

      try {
        const state = get();
        const data = await fetchRecentlySearched(userId, limit);

        // Discard stale responses from older concurrent calls
        if (mySeq !== reloadSeq) return;

        const uniqueItemsMap = new Map<string, SearchHistoryNode>();

        for (const obj of data) {
          const rawQuery = obj.search_query;
          // Normalize the key so queries with different URL encodings are treated as the same entry
          const normalizedKey = buildSearchRouteQuery(rawQuery);
          const itemDate = new Date(obj.updatedAt);
          const existing = uniqueItemsMap.get(normalizedKey);

          if (!existing || existing.date.getTime() < itemDate.getTime()) {
            uniqueItemsMap.set(normalizedKey, {
              key: normalizedKey,
              path: normalizedKey,
              title: formatSearchQueryTitle(rawQuery, state.availableArchitecture),
              titleText: rawQuery,
              isLeaf: true,
              date: itemDate,
            });
          }
        }

        set({
          historyData: Array.from(uniqueItemsMap.values()).sort(
            (a, b) => b.date.getTime() - a.date.getTime()
          ),
        });
      } catch (error) {
        Log.error('Failed to load search history data', error);
      }
    },

    loadLabelData: async () => {
      const state = get();
      // If already loaded or loading, skip
      if (state.hwTypes.length > 0 || state.isLabelDataLoading) {
        return;
      }

      set({ isLabelDataLoading: true });

      try {
        const [hwTypes, subsystemsResponse] = await Promise.all([
          GLOB.menuService.hwTypesLoaded.promise,
          getAllSubsystems()
        ]);

        set({
          hwTypes,
          subsystems: subsystemsResponse.data.data,
          isLabelDataLoading: false
        });
      } catch (error) {
        Log.error('Failed to load label data', error);
        set({ isLabelDataLoading: false });
      }
    },
  };
});
